import React, { useEffect, useMemo, useState } from 'react'
import { adminRequest } from '../api/client'
import { Card, EmptyState, PageTitle, SearchField, StatusBadge, formatCurrency, formatDate } from '../components/Ui'
import AdminFormModal, { FormField, FormGrid } from '../components/AdminFormModal'

export function Payments({ navigate }) {
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let disposed = false
    adminRequest('/api/v1/admin/payments')
      .then((response) => { if (!disposed) setItems(response?.data || []) })
      .catch((nextError) => { if (!disposed) setError(nextError?.message || '결제 내역을 불러오지 못했습니다.') })
      .finally(() => { if (!disposed) setLoading(false) })
    return () => { disposed = true }
  }, [])

  const rows = useMemo(() => items.filter((item) => !search || [item.paymentNo,item.orderNo,item.customerName,item.customerNickname,item.directorName,item.portonePaymentId,item.transactionId].join(' ').toLowerCase().includes(search.toLowerCase())), [items, search])
  const paid = items.filter((item) => item.status === 'PAID')
  const paidAmount = paid.reduce((sum, item) => sum + Number(item.amount || 0), 0)
  const confirmedFee = items.reduce((sum, item) => sum + Number(item.pgFeeAmount || 0), 0)

  return <>
    <PageTitle eyebrow="PAYMENTS · LIVE DB" title="결제 관리" description="difan_dev의 주문·결제 원장과 PortOne 식별자를 조회합니다." />
    <div className="metric-grid finance-metrics"><div className="metric-card metric-green"><span>결제 완료</span><strong>{paid.length}건</strong><small>{formatCurrency(paidAmount)}</small></div><div className="metric-card metric-orange"><span>전체 결제 레코드</span><strong>{items.length}건</strong><small>READY/PAID/환불 상태 포함</small></div><div className="metric-card metric-blue"><span>PG 수수료 확정액</span><strong>{formatCurrency(confirmedFee)}</strong><small>DB에 확정된 값 합계</small></div><div className="metric-card metric-gold"><span>데이터 원본</span><strong>LIVE</strong><small>PostgreSQL difan_dev</small></div></div>
    <Card className="table-card"><div className="table-toolbar"><SearchField value={search} onChange={setSearch} placeholder="결제번호, 주문번호, 고객, 디렉터 검색" /></div><div className="table-result-bar"><span>검색 결과 <strong>{rows.length}</strong>건</span><small>임의 결제 예시값을 사용하지 않습니다.</small></div>{loading ? <p className="help-copy">실제 결제 데이터를 불러오는 중입니다.</p> : error ? <EmptyState title="결제 조회 실패" description={error} /> : rows.length === 0 ? <EmptyState title="결제 내역이 없습니다." description="실제 결제가 생성되면 이곳에 표시됩니다." /> : <div className="table-scroll"><table className="data-table"><thead><tr><th>결제번호</th><th>주문</th><th>고객 / 디렉터</th><th>금액</th><th>PG / 수단</th><th>PortOne</th><th>PG 수수료</th><th>상태</th><th>결제일</th></tr></thead><tbody>{rows.map((item) => <tr className="clickable-row" key={item.id} onClick={() => navigate?.(`/payments/${item.id}`)}><td><strong>{item.paymentNo}</strong></td><td>{item.orderNo}</td><td><strong>{item.customerNickname || item.customerName || '-'}</strong><small>{item.directorName || '-'}</small></td><td><strong>{formatCurrency(item.amount)}</strong></td><td>{item.pgProvider || item.provider || '-'}<small>{item.method || '-'}</small></td><td><small className="mono">{item.portonePaymentId || '-'}</small><small className="mono">{item.transactionId || '-'}</small></td><td>{item.pgFeeAmount == null ? '미확정' : formatCurrency(item.pgFeeAmount)}</td><td><StatusBadge status={item.status} /></td><td>{formatDate(item.paidAt || item.createdAt, true)}</td></tr>)}</tbody></table></div>}</Card>
  </>
}

function currentLocalMonth() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

export function settlementPeriodRange(periodMonth) {
  const match = String(periodMonth || '').match(/^(\d{4})-(0[1-9]|1[0-2])$/)
  if (!match) return '-'
  const year = Number(match[1])
  const month = Number(match[2])
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const mm = String(month).padStart(2, '0')
  return `${year}.${mm}.01 ~ ${year}.${mm}.${String(lastDay).padStart(2, '0')}`
}

function settlementStatusLabel(status) {
  return ({ DRAFT: '검토 중', PAYABLE: '지급 가능', PAID: '지급 완료' })[status] || status
}

function signedCurrency(value) {
  const amount = Number(value || 0)
  return `${amount > 0 ? '+' : amount < 0 ? '-' : ''}${formatCurrency(Math.abs(amount))}`
}

export function Settlements({ navigate }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const [editor, setEditor] = useState(null)

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await adminRequest('/api/v1/admin/settlements')
      setItems(response?.data || [])
    } catch (nextError) {
      setError(nextError?.message || '정산 내역을 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const openCreate = () => {
    setNotice('')
    setEditor({ periodMonth: currentLocalMonth(), error: '' })
  }

  const generate = async (event) => {
    event.preventDefault()
    if (busy || !editor) return
    const periodMonth = editor.periodMonth
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(periodMonth || '')) {
      setEditor((current) => ({ ...current, error: '정산 대상 월을 선택해주세요.' }))
      return
    }
    const existing = items.find((item) => item.periodMonth === periodMonth)
    if (existing) {
      setEditor((current) => ({ ...current, error: `이미 ${existing.batchNo} 회차가 생성되어 있습니다.` }))
      return
    }

    setBusy(true)
    setEditor((current) => ({ ...current, error: '' }))
    try {
      const response = await adminRequest('/api/v1/admin/settlements/generate', {
        method: 'POST',
        headers: { 'Idempotency-Key': `admin-settlement-${periodMonth}-${Date.now()}` },
        body: { periodMonth },
      })
      setEditor(null)
      setNotice(`${periodMonth} 정산 회차를 실제 DB에 생성했습니다. 대상 기간은 ${settlementPeriodRange(periodMonth)}로 고정됩니다.`)
      await load()
      if (response?.data?.id) navigate?.(`/settlements/${response.data.id}`)
    } catch (nextError) {
      setEditor((current) => ({ ...current, error: nextError?.message || '정산 회차 생성에 실패했습니다.' }))
    } finally {
      setBusy(false)
    }
  }

  return <>
    <PageTitle eyebrow="SETTLEMENTS · LIVE DB" title="정산 관리" description="프로젝트 최종 확정일을 기준으로 달력 월 단위 정산 회차를 생성하고 실제 정산 원장을 관리합니다." actions={<button type="button" className="button primary" onClick={openCreate}>새 정산 회차 생성</button>} />
    <div className="policy-callout"><strong>정산 회차 기간</strong><span>정산월을 선택하면 대상 기간은 해당 월 1일 00:00부터 다음 달 1일 00:00 직전까지로 자동 고정됩니다. 임의의 시작일·종료일을 따로 입력하지 않으며, 편입 기준일은 프로젝트 최종 확정일입니다.</span></div>
    <div className="policy-callout"><strong>실데이터 정산</strong><span>결제금액 - 환불 - 실제 PG 수수료 - 플랫폼 수수료(초기 0원) +/- 조정액 = 디렉터 정산액. 브라우저 localStorage에는 정산 원장을 저장하지 않습니다.</span></div>
    {notice && <div className="admin-notice"><strong>안내</strong><span>{notice}</span></div>}
    <Card className="table-card"><div className="table-result-bar"><span>정산 회차 <strong>{items.length}</strong>건</span><small>source: settlement_batches + settlement_items</small></div>{loading ? <p className="help-copy">실제 정산 회차를 불러오는 중입니다.</p> : error ? <EmptyState title="정산 조회 실패" description={error} /> : items.length === 0 ? <EmptyState title="정산 회차가 없습니다." description="새 정산 회차 생성에서 대상 월과 자동 계산된 기간을 확인한 뒤 생성할 수 있습니다." /> : <div className="table-scroll"><table className="data-table"><thead><tr><th>정산 회차</th><th>정산 월 / 대상 기간</th><th>디렉터</th><th>결제금액</th><th>환불</th><th>PG 수수료</th><th>플랫폼 수수료</th><th>조정</th><th>지급액</th><th>상태</th><th>확정 / 지급일</th></tr></thead><tbody>{items.map((item) => <tr className="clickable-row" key={item.id} onClick={() => navigate?.(`/settlements/${item.id}`)}><td><strong>{item.batchNo}</strong></td><td><strong>{item.periodMonth}</strong><small>{settlementPeriodRange(item.periodMonth)}</small></td><td>{item.directorCount || 0}명</td><td>{formatCurrency(item.grossAmount)}</td><td>-{formatCurrency(item.refundAmount)}</td><td>-{formatCurrency(item.pgFee)}</td><td>{formatCurrency(item.platformFee)}</td><td>{signedCurrency(item.adjustmentAmount)}</td><td><strong>{formatCurrency(item.netAmount)}</strong></td><td><StatusBadge status={item.status}>{settlementStatusLabel(item.status)}</StatusBadge></td><td><small>확정 {formatDate(item.payableAt, true)}</small><small>지급 {formatDate(item.paidAt, true)}</small></td></tr>)}</tbody></table></div>}</Card>
    <div className="finance-note-grid"><Card title="정산 원장 원칙"><ul className="bullet-list"><li>동일한 정산월은 중복 생성하지 않음</li><li>PAID 정산 기록은 직접 수정하지 않음</li><li>사후 환불은 다음 회차 adjustment로 반영</li><li>실제 지급 계좌·금액·시각은 payout snapshot으로 보존</li></ul></Card><Card title="정산 대상 자격"><p>PG 수수료가 확정되고, 지급계좌가 검증되며, 환불·분쟁 보류가 없는 거래만 서버가 정산 회차에 편입합니다.</p></Card></div>

    <AdminFormModal open={Boolean(editor)} title="새 정산 회차 생성" description="정산월을 선택하면 해당 월의 최종 확정 거래를 서버가 검증해 회차에 편입합니다. 생성 후 정산월과 대상 기간은 임의 변경하지 않습니다." onClose={() => !busy && setEditor(null)} onSubmit={generate} submitLabel={busy ? '생성 중…' : '회차 생성'}>
      {editor && <FormGrid>
        <FormField label="정산 대상 월" required wide><input type="month" required value={editor.periodMonth} onChange={(event) => setEditor({ ...editor, periodMonth: event.target.value, error: '' })} /></FormField>
        <FormField label="대상 기간" wide><input readOnly value={settlementPeriodRange(editor.periodMonth)} /></FormField>
        <FormField label="편입 기준" wide><input readOnly value="프로젝트 최종 확정일" /></FormField>
        <div className="policy-callout" style={{ gridColumn: '1 / -1' }}><strong>서버 자동 검증</strong><span>PAID 결제, 확정 PG 수수료, 검증된 사업자명 계좌, 환불·분쟁 보류 여부를 확인한 뒤 지급 가능한 거래만 집계합니다.</span></div>
        {editor.error && <div className="admin-notice" style={{ gridColumn: '1 / -1' }}><strong>확인 필요</strong><span>{editor.error}</span></div>}
      </FormGrid>}
    </AdminFormModal>
  </>
}
